import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function rand(min, max) { return min + Math.random() * (max - min); }
function randSign()      { return Math.random() < 0.5 ? 1 : -1; }

function wander(el, maxPct, opts = {}) {
    const {
        minDur    = 1.8,  maxDur    = 5.0,
        minPause  = 300,  maxPause  = 2200,
        dartChance = 0.12,
    } = opts;

    function step() {
        const dart  = Math.random() < dartChance;
        const angle = Math.random() * Math.PI * 2;
        const r     = Math.sqrt(Math.random()) * maxPct;

        gsap.to(el, {
            xPercent: Math.cos(angle) * r,
            yPercent: Math.sin(angle) * r,
            duration: dart ? rand(0.25, 0.55) : rand(minDur, maxDur),
            ease:     dart ? 'power3.out'     : 'sine.inOut',
            onComplete: () => setTimeout(step, dart ? rand(80, 350) : rand(minPause, maxPause)),
        });
    }

    setTimeout(step, rand(200, 1400));
}

function idleAnimate(el, opts = {}) {
    const {
        rot      = 8,
        yPct     = 10,
        scale    = 0.06,
        minDur   = 1.8,  maxDur   = 4.8,
        minPause = 500,  maxPause = 3800,
        initDelay,
    } = opts;

    gsap.set(el, { transformOrigin: '50% 50%' });

    const POOL    = ['rot', 'y', 'scale', 'rot+y', 'wiggle', 'none', 'none'];
    const WEIGHTS = [  3,    3,    2,       2,        2,       2,      2  ];
    const W_TOTAL = WEIGHTS.reduce((a, b) => a + b, 0);

    function pick() {
        let r = Math.random() * W_TOTAL;
        for (let i = 0; i < POOL.length; i++) { r -= WEIGHTS[i]; if (r <= 0) return POOL[i]; }
        return 'none';
    }

    function step() {
        const beh = pick();
        const dur = rand(minDur, maxDur);
        const h   = dur * 0.5;
        const tl  = gsap.timeline({ onComplete: () => setTimeout(step, rand(minPause, maxPause)) });

        if (beh === 'rot') {
            const v = rand(rot * 0.25, rot) * randSign();
            tl.to(el, { rotation: v, duration: h, ease: 'power1.out' })
              .to(el, { rotation: 0, duration: h, ease: 'power1.in'  });
        } else if (beh === 'y') {
            const v = rand(yPct * 0.25, yPct) * randSign();
            tl.to(el, { yPercent: v, duration: h, ease: 'sine.out' })
              .to(el, { yPercent: 0, duration: h, ease: 'sine.in'  });
        } else if (beh === 'scale') {
            const v = 1 + rand(scale * 0.3, scale) * randSign();
            tl.to(el, { scale: v, duration: h, ease: 'power1.out' })
              .to(el, { scale: 1, duration: h, ease: 'power1.in'  });
        } else if (beh === 'rot+y') {
            const rv = rand(rot * 0.2, rot * 0.65) * randSign();
            const yv = rand(yPct * 0.2, yPct * 0.65) * randSign();
            tl.to(el, { rotation: rv, yPercent: yv, duration: h, ease: 'power1.out' })
              .to(el, { rotation: 0,  yPercent: 0,  duration: h, ease: 'power1.in'  });
        } else if (beh === 'wiggle') {
            const w = rand(rot * 0.4, rot * 0.8);
            const s = 0.10;
            tl.to(el, { rotation:  w,         duration: s,       ease: 'none' })
              .to(el, { rotation: -w,         duration: s * 2,   ease: 'none' })
              .to(el, { rotation:  w * 0.6,   duration: s * 1.5, ease: 'none' })
              .to(el, { rotation: -w * 0.3,   duration: s,       ease: 'none' })
              .to(el, { rotation:  0,         duration: s * 0.8, ease: 'power2.out' });
        } else {
            tl.to({}, { duration: rand(0.4, 1.4) });
        }
    }

    setTimeout(step, initDelay ?? rand(400, 3200));
}

function runLogoAnimation(container) {
    const svg = container.querySelector('.logo-svg');
    if (!svg) return;

    const lets     = svg.querySelector('.logo-lets');
    const innovate = svg.querySelector('.logo-innovate');
    if (!lets || !innovate) return;

    const W = svg.getBoundingClientRect().width || 200;

    const entryTl = gsap.timeline();
    entryTl
        .from(lets,     { x:  W * 1.35, duration: 1.55, ease: 'power3.out' }, 0)
        .from(innovate, { x: -W * 1.1,  duration: 1.55, ease: 'power3.out' }, 0.05);

    entryTl.call(() => {
        const iDot = svg.querySelector('.logo-i-dot');
        const eDot = svg.querySelector('.logo-excl-dot');
        const n1   = svg.querySelector('.logo-n1');
        const n2   = svg.querySelector('.logo-n2');
        const o    = svg.querySelector('.logo-o');
        const v    = svg.querySelector('.logo-v');
        const a    = svg.querySelector('.logo-a');
        const t    = svg.querySelector('.logo-t');
        const e    = svg.querySelector('.logo-e');

        const letters = [iDot, eDot, n1, n2, o, v, a, t, e].filter(Boolean);

        function startIdle() {
            gsap.killTweensOf(letters);

            if (iDot) wander(iDot, 260, { minDur: 1.8, maxDur: 5.5, minPause: 300,  maxPause: 2000, dartChance: 0.15 });
            if (eDot) wander(eDot, 210, { minDur: 2.2, maxDur: 6.0, minPause: 500,  maxPause: 2600, dartChance: 0.10 });

            if (n1) idleAnimate(n1, { rot: 9,  yPct: 11, scale: 0.05, minDur: 2.0, maxDur: 4.5, minPause: 500, maxPause: 3500, initDelay: rand(300,  1400) });
            if (n2) idleAnimate(n2, { rot: 7,  yPct: 12, scale: 0.06, minDur: 1.7, maxDur: 4.0, minPause: 600, maxPause: 3200, initDelay: rand(900,  2200) });
            if (o)  idleAnimate(o,  { rot: 5,  yPct: 7,  scale: 0.09, minDur: 2.2, maxDur: 5.2, minPause: 700, maxPause: 4000, initDelay: rand(200,  1200) });
            if (v)  idleAnimate(v,  { rot: 11, yPct: 13, scale: 0.04, minDur: 1.5, maxDur: 3.5, minPause: 500, maxPause: 3000, initDelay: rand(1400, 3200) });
            if (a)  idleAnimate(a,  { rot: 6,  yPct: 9,  scale: 0.07, minDur: 2.5, maxDur: 5.5, minPause: 800, maxPause: 4200, initDelay: rand(600,  2500) });
            if (t)  idleAnimate(t,  { rot: 8,  yPct: 8,  scale: 0.05, minDur: 1.9, maxDur: 4.2, minPause: 600, maxPause: 3600, initDelay: rand(1800, 3800) });
            if (e)  idleAnimate(e,  { rot: 7,  yPct: 10, scale: 0.06, minDur: 2.0, maxDur: 4.5, minPause: 700, maxPause: 3400, initDelay: rand(800,  2800) });
        }

        startIdle();

        let hovered       = false;
        let restartTimer  = null;

        container.addEventListener('mouseenter', () => {
            hovered = true;
            clearTimeout(restartTimer);
            gsap.killTweensOf(letters);
            gsap.to(letters, {
                xPercent: 0, yPercent: 0,
                x: 0, y: 0,
                rotation: 0, scale: 1,
                duration: 0.35,
                ease: 'power2.out',
                overwrite: true,
                stagger: 0.02,
            });
        });

        container.addEventListener('mouseleave', () => {
            hovered = false;
            restartTimer = setTimeout(() => {
                if (!hovered) startIdle();
            }, 200);
        });
    }, [], '>');
}

export function initLogos() {
    const navLogo = document.querySelector('.site-nav .logo-wrap');
    if (navLogo) runLogoAnimation(navLogo);

    const footerLogo = document.querySelector('.site-footer .logo-wrap');
    if (footerLogo) {
        ScrollTrigger.create({
            trigger: footerLogo,
            start: 'top 88%',
            once: true,
            onEnter: () => runLogoAnimation(footerLogo),
        });
    }
}
